import React from "react";
import PropTypes from "prop-types";
import Button from "../../../../components/UI/Button";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CalendarIcon,
  ShieldCheckIcon,
  UserIcon,
} from "../../../../Icons";
import { displayDateUS } from "../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../patientPageLayout";
import { PATIENT_THEME } from "../../patientTheme";
import {
  NURSE_BILLING_CODE_OPTIONS,
  TIME_OPTIONS_15,
  normalizeTimeOption,
  time12ToMinutes,
} from "./nurseNotes/nurseVisitCommon";

/**
 * The shared chrome every patient admission document is built from: the sheet
 * itself, its section cards, its field styles, and the workflow footer.
 *
 * Each form only supplies its own body, so a new form looks and behaves like
 * the rest of the module without restating any of this.
 */

/** Width of the printed sheet — keeps a clear margin on both sides. */
export const SHEET = "mx-auto w-full max-w-4xl";

export const inputBase = `w-full px-2 text-[12px] text-slate-900 border border-slate-200 rounded bg-white outline-none disabled:bg-slate-50 disabled:text-slate-500 ${PATIENT_THEME.fieldFocus}`;
export const fieldClass = `${inputBase} h-7`;
export const textareaClass = `${inputBase} py-1.5 resize-y`;
/** Borderless control that matches HeaderCell height (one-line identity strip). */
export const stripControlClass =
  "w-full h-4 min-h-4 max-h-4 text-[10px] font-bold text-slate-900 bg-transparent border-0 p-0 pr-4 outline-none appearance-none leading-4 truncate";
export const labelClass =
  "block text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5";
export const checkboxClass =
  "mt-px h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-indigo-500 focus:ring-indigo-200 disabled:opacity-60";

export const ADMISSION_FORM_STATUS_META = {
  pending: {
    label: "Pending",
    wrap: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  },
  send_to_office: {
    label: "Sent to Office",
    wrap: "bg-sky-50 text-sky-800 border-sky-200",
    dot: "bg-sky-500",
  },
  to_be_signed: {
    label: "To Be Sign",
    wrap: "bg-violet-50 text-violet-800 border-violet-200",
    dot: "bg-violet-500",
  },
  to_be_corrected: {
    label: "To Be Corrected",
    wrap: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
  },
  completed: {
    label: "Completed",
    wrap: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot: "bg-emerald-500",
  },
};

export const StatusPill = ({ status }) => {
  const meta = ADMISSION_FORM_STATUS_META[status] || {
    label: status || "—",
    wrap: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.wrap}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

StatusPill.propTypes = { status: PropTypes.string };

export const Section = ({ icon: Icon, title, children, className = "" }) => (
  <section
    className={`rounded-md border border-slate-200 overflow-hidden ${className}`}
  >
    <div
      className={`flex items-center gap-1.5 px-3 py-1.5 border-b ${PATIENT_THEME.section.head}`}
    >
      {Icon && <Icon size={12} className={PATIENT_THEME.section.icon} />}
      <h3
        className={`text-[10px] font-semibold uppercase tracking-wide ${PATIENT_THEME.section.title}`}
      >
        {title}
      </h3>
    </div>
    <div className="p-3">{children}</div>
  </section>
);

Section.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
};

export const SubHeading = ({ children }) => (
  <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
    {children}
  </p>
);

SubHeading.propTypes = { children: PropTypes.node };

export const Field = ({ label, span = "", children }) => (
  <div className={span}>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  span: PropTypes.string,
  children: PropTypes.node,
};

/**
 * Shared checkbox option grid — same pattern on nurse notes / admission docs.
 * Use inside a Section under SubHeading for option groups.
 */
export const CheckboxGrid = ({
  options,
  values,
  canEdit,
  onToggle,
  columns = 2,
  layout = "auto",
}) => {
  const useFlexWrap = columns >= 3 && layout !== "grid";
  const colClass =
    {
      1: "grid-cols-1",
      2: "grid-cols-1 sm:grid-cols-2",
      3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
      4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    }[columns] || "grid-cols-1 sm:grid-cols-2";
  const selected = Array.isArray(values) ? values : [];
  return (
    <div
      className={
        useFlexWrap ? "flex flex-wrap gap-1.5" : `grid ${colClass} gap-1.5`
      }
    >
      {options.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <label
            key={opt}
            className={`inline-flex items-center gap-2 text-[12px] text-slate-700 rounded-md border px-2.5 py-2 ${
              useFlexWrap ? "shrink-0 max-w-full" : "min-w-0 w-full"
            } ${canEdit ? "cursor-pointer" : "cursor-default"} ${
              checked
                ? "border-indigo-300 bg-indigo-50/60"
                : "border-slate-200 bg-white"
            }`}
          >
            <input
              type="checkbox"
              className={`${checkboxClass} mt-0 shrink-0`}
              checked={checked}
              disabled={!canEdit}
              onChange={() => onToggle(opt)}
            />
            <span className="leading-snug break-words">{opt}</span>
          </label>
        );
      })}
    </div>
  );
};

CheckboxGrid.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.string),
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  columns: PropTypes.number,
  layout: PropTypes.oneOf(["auto", "grid"]),
};

/**
 * Mutually exclusive Yes/No (or any option set) with the same card chrome as
 * CheckboxGrid — only one option can be selected at a time.
 */
export const ExclusiveChoiceGrid = ({
  options,
  value,
  canEdit,
  onChange,
  columns = 2,
  name,
}) => {
  // Flex-wrap chips size to content so long labels (e.g. "Mod. Independent",
  // "Remained Same", "Outpatient Therapy") never clip into the next option.
  const useFlexWrap = columns >= 3;
  const colClass =
    {
      1: "grid-cols-1",
      2: "grid-cols-1 sm:grid-cols-2",
      3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
      4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    }[columns] || "grid-cols-1 sm:grid-cols-2";
  const current = value == null ? "" : String(value);

  return (
    <div
      className={
        useFlexWrap
          ? "flex flex-wrap gap-1.5"
          : `grid ${colClass} gap-1.5`
      }
      role="radiogroup"
      aria-label={name || "Choice"}
    >
      {options.map((opt) => {
        const checked = current === opt;
        return (
          <label
            key={opt}
            className={`inline-flex items-center gap-2 text-[12px] text-slate-700 rounded-md border px-2.5 py-2 ${
              useFlexWrap ? "shrink-0 max-w-full" : "min-w-0 w-full"
            } ${canEdit ? "cursor-pointer" : "cursor-default"} ${
              checked
                ? "border-indigo-300 bg-indigo-50/60"
                : "border-slate-200 bg-white"
            }`}
          >
            <input
              type="radio"
              name={name || undefined}
              className={`${checkboxClass} mt-0 shrink-0`}
              checked={checked}
              disabled={!canEdit}
              onChange={() => {
                if (!canEdit) return;
                onChange(opt);
              }}
              onClick={(e) => {
                // Re-click active option clears selection.
                if (canEdit && checked) {
                  e.preventDefault();
                  onChange("");
                }
              }}
            />
            <span className="leading-snug break-words">{opt}</span>
          </label>
        );
      })}
    </div>
  );
};

ExclusiveChoiceGrid.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  columns: PropTypes.number,
  name: PropTypes.string,
};

/**
 * A titled checkbox with explanatory copy underneath, as a full-width card —
 * the shape every consent, acknowledgement and reason list on these forms uses.
 * `highlight` tints the card once ticked, for choices that reveal more fields.
 */
export const CheckboxCard = ({
  item,
  checked,
  disabled,
  onChange,
  highlight = false,
}) => (
  <label
    className={`flex items-start gap-2.5 rounded-md border px-3 py-2 transition-colors ${
      highlight && checked
        ? "border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-200"
        : "border-slate-200 hover:border-slate-300"
    } ${disabled ? "cursor-default" : "cursor-pointer"}`}
  >
    <input
      type="checkbox"
      className={checkboxClass}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="min-w-0">
      <span className="block text-[10px] font-bold text-slate-800">
        {item.title}
      </span>
      <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
        {item.description}
      </span>
    </span>
  </label>
);

CheckboxCard.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  highlight: PropTypes.bool,
};

/** `dense` squeezes every cell onto one row, as the paper form does. */
export const HeaderCell = ({ label, value, dense = false }) => (
  <div className="min-w-0">
    <div
      className={`font-semibold uppercase tracking-wider text-slate-400 leading-none ${
        dense ? "text-[7px]" : "text-[8px]"
      }`}
    >
      {label}
    </div>
    <div
      className={`mt-0.5 h-4 flex items-center font-bold text-slate-900 min-w-0 ${
        dense ? "text-[10px]" : "text-[12px]"
      }`}
    >
      <span className="truncate">{value || "—"}</span>
    </div>
  </div>
);

HeaderCell.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  dense: PropTypes.bool,
};

/** Label + fixed-height control row — matches HeaderCell / CaregiverSelectField. */
export const HeaderControlCell = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
      {label}
    </div>
    <div className="mt-0.5 h-4 flex items-center min-w-0">{children}</div>
  </div>
);

HeaderControlCell.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
};

/** Filled-by label used in the common identity strip (Physician Order pattern). */
export const caregiverLabel = (form) => {
  const name = form?.filledByName || "";
  const filledRole = form?.filledByRole || "";
  if (name && filledRole) return `${name} (${filledRole})`;
  return name || filledRole || "—";
};

/**
 * Shared top identity row: patient, MRN, DOB, caregiver, date.
 * Matches Physician Order / SPO. Pass `dateSlot` for an editable date picker.
 * Optional `visitMeta` adds Billing Code + Time In / Time Out (nurse notes).
 */
export const FormIdentityStrip = ({
  patientName,
  mrn,
  dateOfBirth,
  form,
  dateLabel = "Date",
  dateValue,
  dateSlot = null,
  patientSlot = null,
  caregiverSlot = null,
  visitMeta = null,
}) => {
  const inlineTimes = Boolean(visitMeta) && visitMeta.showBillingCode === false;
  return (
    <section className="rounded-md border border-slate-200 overflow-hidden bg-slate-50/40">
      <div className="overflow-x-auto">
        <div
          className={`grid gap-x-2.5 items-end px-3 py-1.5 ${
            inlineTimes ? "grid-cols-7 min-w-[860px]" : "grid-cols-5 min-w-[640px]"
          }`}
        >
          {patientSlot || (
            <HeaderCell
              label="Patient Name"
              value={patientName || form?.patientName}
              dense
            />
          )}
          <HeaderCell label="MR Number" value={mrn} dense />
          <HeaderCell label="Date of Birth" value={dateOfBirth} dense />
          {caregiverSlot || (
            <HeaderCell
              label="Caregiver Name"
              value={caregiverLabel(form)}
              dense
            />
          )}
          {dateSlot || <HeaderCell label={dateLabel} value={dateValue} dense />}
          {inlineTimes ? (
            <>
              <TimeAmPmField
                label="Time In"
                name="timeIn"
                value={visitMeta.timeIn}
                canEdit={visitMeta.canEdit}
                printMode={visitMeta.printMode}
                onChange={(next) => visitMeta.onTimeInChange?.(next)}
                dense
              />
              <TimeAmPmField
                label="Time Out"
                name="timeOut"
                value={visitMeta.timeOut}
                canEdit={visitMeta.canEdit}
                printMode={visitMeta.printMode}
                minTime={visitMeta.timeIn}
                onChange={(next) => visitMeta.onTimeOutChange?.(next)}
                dense
              />
            </>
          ) : null}
        </div>
      </div>
      {inlineTimes && visitMeta.error ? (
        <p className="px-3 pb-1.5 text-[10px] font-medium text-rose-600">
          {visitMeta.error}
        </p>
      ) : null}
      {visitMeta && !inlineTimes ? <FormVisitMetaRow {...visitMeta} /> : null}
    </section>
  );
};

FormIdentityStrip.propTypes = {
  patientName: PropTypes.string,
  mrn: PropTypes.string,
  dateOfBirth: PropTypes.string,
  form: PropTypes.object,
  dateLabel: PropTypes.string,
  dateValue: PropTypes.string,
  dateSlot: PropTypes.node,
  patientSlot: PropTypes.node,
  caregiverSlot: PropTypes.node,
  visitMeta: PropTypes.object,
};

const timeFieldClass = `${fieldClass} min-w-[7.5rem] max-h-7 py-0 leading-7 appearance-none`;

/**
 * Single Time In / Time Out picker — 15-minute AM/PM slots
 * (matches skilled nurse visit note dropdown).
 */
export const TimeAmPmField = ({
  label,
  value,
  canEdit,
  printMode = false,
  onChange,
  name,
  minTime = "",
  /** When false, only the control is rendered (wrap with Field for shared labels). */
  showLabel = true,
  controlClassName,
  dense = false,
}) => {
  const normalized = normalizeTimeOption(value);
  const minMinutes = minTime ? time12ToMinutes(minTime) : null;
  const baseOptions =
    minMinutes == null
      ? TIME_OPTIONS_15
      : TIME_OPTIONS_15.filter((opt) => {
          const mins = time12ToMinutes(opt);
          return mins != null && mins > minMinutes;
        });
  const options =
    normalized && !baseOptions.includes(normalized)
      ? [normalized, ...baseOptions]
      : baseOptions;
  const selectClass = controlClassName || (dense ? stripControlClass : timeFieldClass);

  if (printMode || !canEdit) {
    if (!showLabel) {
      return (
        <div
          className={`${
            dense ? "h-4 text-[10px]" : "h-7 text-[12px]"
          } flex items-center font-semibold text-slate-900 truncate`}
        >
          {normalized || "—"}
        </div>
      );
    }
    return (
      <div className="min-w-0">
        <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
          {label}
        </div>
        <div
          className={`mt-0.5 flex items-center font-bold text-slate-900 truncate ${
            dense ? "h-4 text-[10px]" : "text-[11px]"
          }`}
        >
          {normalized || "—"}
        </div>
      </div>
    );
  }

  const selectEl = (
    <select
      name={name}
      aria-label={label}
      className={selectClass}
      value={normalized}
      onChange={(e) => onChange?.(e.target.value)}
    >
      <option value="">Select time…</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );

  if (!showLabel) return selectEl;

  return (
    <div className="min-w-0">
      <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
        {label}
      </div>
      <div className={`mt-0.5 min-w-0 ${dense ? "h-4 flex items-center" : ""}`}>
        {selectEl}
      </div>
    </div>
  );
};

TimeAmPmField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  minTime: PropTypes.string,
  showLabel: PropTypes.bool,
  controlClassName: PropTypes.string,
  dense: PropTypes.bool,
};

export const FormVisitMetaRow = ({
  billingCode = "",
  timeIn = "",
  timeOut = "",
  canEdit = false,
  printMode = false,
  onBillingCodeChange,
  onTimeInChange,
  onTimeOutChange,
  error = "",
  billingOptions = NURSE_BILLING_CODE_OPTIONS,
  showBillingCode = true,
}) => (
  <div className="border-t border-slate-200 bg-indigo-50/40 px-3 py-1.5">
    <div
      className={`grid gap-x-3 items-end ${
        showBillingCode
          ? "grid-cols-[minmax(0,1.6fr)_minmax(0,0.7fr)_minmax(0,0.7fr)]"
          : "grid-cols-2"
      }`}
    >
      {showBillingCode ? (
        <div className="min-w-0">
          <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">
            Billing Code
          </div>
          {printMode || !canEdit ? (
            <div className="h-7 flex items-center text-[11px] font-semibold text-slate-900 truncate">
              {billingOptions.find((opt) => opt.value === billingCode)?.label ||
                billingCode ||
                "—"}
            </div>
          ) : (
            <select
              name="billingCode"
              className={fieldClass}
              value={billingCode}
              onChange={(e) => onBillingCodeChange?.(e.target.value)}
            >
              <option value="">Select billing code…</option>
              {billingOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
      ) : null}
      <TimeAmPmField
        label="Time In"
        name="timeIn"
        value={timeIn}
        canEdit={canEdit}
        printMode={printMode}
        onChange={(next) => onTimeInChange?.(next)}
      />
      <TimeAmPmField
        label="Time Out"
        name="timeOut"
        value={timeOut}
        canEdit={canEdit}
        printMode={printMode}
        minTime={timeIn}
        onChange={(next) => onTimeOutChange?.(next)}
      />
    </div>
    {error ? (
      <p className="mt-1.5 text-[10px] font-medium text-rose-600">{error}</p>
    ) : null}
  </div>
);

FormVisitMetaRow.propTypes = {
  billingCode: PropTypes.string,
  timeIn: PropTypes.string,
  timeOut: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onBillingCodeChange: PropTypes.func,
  onTimeInChange: PropTypes.func,
  onTimeOutChange: PropTypes.func,
  error: PropTypes.string,
  billingOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  ),
  showBillingCode: PropTypes.bool,
};

/** Back button, patient identity and status — outside the printed sheet. */
export const FormPageHeader = ({
  patientName,
  mrn,
  status,
  onBack,
  backLabel = "Back",
}) => (
  <div className={`${SHEET} flex items-center gap-3`}>
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center justify-center h-8 w-8 shrink-0 bg-white rounded-lg border border-slate-200 shadow-sm text-slate-500 hover:text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition-colors"
      aria-label={backLabel}
      title={backLabel}
    >
      <ArrowLeftIcon size={15} />
    </button>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-semibold text-slate-700 truncate">
        {patientName || "Patient"}{" "}
        <span className="font-normal text-slate-400">· MRN {mrn || "—"}</span>
      </p>
    </div>
    <StatusPill status={status} />
  </div>
);

FormPageHeader.propTypes = {
  patientName: PropTypes.string,
  mrn: PropTypes.string,
  status: PropTypes.string,
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
};

/** Provenance line under the header strip: who filled it, schedule date, who reviewed. */
export const FormMetaRow = ({ form, printMode = false }) => {
  const sentAt = form?.sentToOfficeAt || form?.submittedAt || null;
  const scheduleDate =
    form?.scheduleDate ||
    form?.scheduledVisit?.visitDate ||
    form?.visitDate ||
    null;
  // Slate-400 is deliberately faint on screen but prints washed out.
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] ${
        printMode ? "text-slate-600" : "text-slate-400"
      }`}
    >
      <span className="inline-flex items-center gap-1">
        <UserIcon size={11} /> Filled by {form?.filledByName || "—"}
        {form?.filledByRole ? ` (${form.filledByRole})` : ""}
      </span>
      <span className="inline-flex items-center gap-1">
        <CalendarIcon size={11} /> Schedule Date{" "}
        {displayDateUS(scheduleDate) || "—"}
      </span>
      {sentAt ? (
        <span className="inline-flex items-center gap-1">
          <CalendarIcon size={11} /> Sent to office {displayDateUS(sentAt)}
        </span>
      ) : null}
      {form?.reviewedByName ? (
        <span className="inline-flex items-center gap-1">
          <ShieldCheckIcon size={11} /> Reviewed by {form.reviewedByName}
        </span>
      ) : null}
    </div>
  );
};

FormMetaRow.propTypes = { form: PropTypes.object, printMode: PropTypes.bool };

const noteClass =
  "flex items-start gap-2 px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md";

/** Why the reader cannot edit is about the app, not about the form. */
export const LockedReasonBanner = ({ reason }) =>
  reason ? (
    <p className={noteClass}>
      <AlertCircleIcon size={12} className="mt-0.5 shrink-0" />
      {reason}
    </p>
  ) : null;

LockedReasonBanner.propTypes = { reason: PropTypes.string };

export const OfficeCommentsNote = ({ comments }) =>
  comments ? (
    <p className={noteClass}>
      <AlertCircleIcon size={12} className="mt-0.5 shrink-0" />
      <span>
        <strong className="font-semibold">Office comments:</strong> {comments}
      </span>
    </p>
  ) : null;

OfficeCommentsNote.propTypes = { comments: PropTypes.string };

/**
 * Always editable for reviewers: a completed form is reopened by marking it
 * To Be Corrected, which requires a comment.
 */
export const OfficeReviewSection = ({ value, onChange }) => (
  <Section icon={ShieldCheckIcon} title="Office Review">
    <Field label="Review Comments">
      <textarea
        className={`${textareaClass} min-h-[52px]`}
        value={value || ""}
        placeholder="Required when marking the form to be corrected"
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  </Section>
);

OfficeReviewSection.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

/**
 * The workflow footer. Clinicians save or send to the office; mapped creators
 * save or mark To Be Sign; co-signers save or send to office; Admin/OA review.
 */
export const FormActionBar = ({
  permissions,
  status,
  canEdit = permissions?.canEdit,
  saving,
  onSubmit,
  printMode = false,
}) => (
  <div
    className={`${SHEET} sticky bottom-0 flex flex-wrap items-center justify-end gap-2 bg-white/95 backdrop-blur-sm border border-slate-200 border-b-0 rounded-t-xl px-4 py-2 ${
      printMode ? "hidden" : ""
    }`}
  >
    {permissions.canToBeSign && !permissions.canSendToOffice && (
      <>
        <Button
          text="Save"
          variant="outline"
          size="sm"
          disabled={!canEdit || Boolean(saving)}
          loading={saving === "save"}
          onClick={() => onSubmit("save")}
        />
        <Button
          text="To Be Sign"
          variant="primary"
          size="sm"
          disabled={!canEdit || Boolean(saving)}
          loading={saving === "to_be_sign"}
          onClick={() => onSubmit("to_be_sign")}
        />
      </>
    )}
    {permissions.canSendToOffice && !permissions.canReview && (
      <>
        <Button
          text="Save"
          variant="outline"
          size="sm"
          disabled={!canEdit || Boolean(saving)}
          loading={saving === "save"}
          onClick={() => onSubmit("save")}
        />
        <Button
          text="Sent to Office"
          variant="primary"
          size="sm"
          disabled={!canEdit || Boolean(saving)}
          loading={saving === "send_to_office"}
          onClick={() => onSubmit("send_to_office")}
        />
      </>
    )}
    {permissions.canReview && (
      <>
        <Button
          text="Save"
          variant="outline"
          size="sm"
          disabled={!canEdit || Boolean(saving)}
          loading={saving === "save"}
          onClick={() => onSubmit("save")}
        />
        <Button
          text="To Be Corrected"
          variant="outline"
          size="sm"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          disabled={Boolean(saving)}
          loading={saving === "to_be_corrected"}
          onClick={() => onSubmit("to_be_corrected")}
        />
        <Button
          text="Mark Completed"
          variant="success"
          size="sm"
          disabled={status === "completed" || Boolean(saving)}
          loading={saving === "completed"}
          onClick={() => onSubmit("completed")}
        />
      </>
    )}
  </div>
);

FormActionBar.propTypes = {
  permissions: PropTypes.object.isRequired,
  status: PropTypes.string,
  canEdit: PropTypes.bool,
  saving: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  printMode: PropTypes.bool,
};

export const FormLoadingSkeleton = () => (
  <div className={`${PATIENT_PAGE_SHELL} animate-pulse`}>
    <div className={`${SHEET} h-9 bg-slate-100 rounded-lg`} />
    <div
      className={`${SHEET} h-[600px] bg-white border border-slate-200 rounded-xl`}
    />
  </div>
);

/** Toast copy for each workflow button, shared so the wording never drifts. */
export const FORM_FLAG_MESSAGES = {
  save: "Form saved",
  send_to_office: "Form sent to the office",
  to_be_sign: "Form marked To Be Sign",
  to_be_corrected: "Form marked to be corrected",
  completed: "Form marked completed",
};
